import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Autocomplete,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ScienceIcon from '@mui/icons-material/Science';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useSnackbar } from 'notistack';

import { ResponsiveModal } from '../common';
import announcementApi, {
  Announcement,
  AnnouncementMeta,
  Audience,
  AudienceFilters,
  AudienceType,
  RecipientUser,
} from '../../services/announcementApi';
import Gate from '../access/Gate';
import { notificationApi } from '../../services/notificationApi';
import PushPreview from './PushPreview';

interface Props {
  open: boolean;
  meta: AnnouncementMeta | null;
  /** Null → create; otherwise edit that draft. */
  initial: Announcement | null;
  onClose: () => void;
  onSaved: () => void;
}

const STEPS = ['محتوا', 'مخاطبان', 'زمان‌بندی و ارسال'];

const TITLE_MAX = 100;
const MESSAGE_MAX = 500;

const emptyAudience: Audience = { type: 'all' };

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('fa-IR');

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in *local* time, not an ISO UTC string. */
const toLocalInput = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AnnouncementComposer: React.FC<Props> = ({ open, meta, initial, onClose, onSaved }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('announcement');
  const [knownTypes, setKnownTypes] = useState<string[]>([]);
  // '' یعنی «خودکار» — بک‌اند دسته را از روی نوع تشخیص می‌دهد.
  const [category, setCategory] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [inApp, setInApp] = useState(true);
  const [push, setPush] = useState(true);
  const [force, setForce] = useState(false);
  const [audience, setAudience] = useState<Audience>(emptyAudience);
  const [scheduledAt, setScheduledAt] = useState('');

  // Recipient picker (audience === 'specific')
  const [recipients, setRecipients] = useState<RecipientUser[]>([]);
  const [recipientOptions, setRecipientOptions] = useState<RecipientUser[]>([]);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientLoading, setRecipientLoading] = useState(false);
  /** Pasted identifiers the backend could not match to a user. */
  const [notFound, setNotFound] = useState<string[]>([]);

  const [preview, setPreview] = useState<{ total: number; withPush: number; byPlatform: Record<string, number> } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Reset the form whenever the modal opens, so a stale draft never leaks in.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError(null);
    setTitle(initial?.title ?? '');
    setMessage(initial?.message ?? '');
    setNotificationType(initial?.notificationType ?? 'announcement');
    setCategory(initial?.category ?? '');
    setLinkUrl(initial?.linkUrl ?? '');
    setImageUrl(initial?.imageUrl ?? '');
    setInApp(initial?.channels?.inApp ?? true);
    setPush(initial?.channels?.push ?? true);
    setForce(initial?.force ?? false);
    setAudience(initial?.audience ?? emptyAudience);
    setScheduledAt(toLocalInput(initial?.scheduledAt));
    setPreview(null);
    setRecipients([]);
    setRecipientOptions([]);
    setRecipientQuery('');
    setNotFound([]);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      const res = await notificationApi.getNotificationTypes();
      if (res.success && res.data?.types) setKnownTypes(res.data.types);
    })();
  }, [open]);

  // Type-ahead lookup, debounced so each keystroke doesn't hit the database.
  const recipientDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (audience.type !== 'specific' || recipientQuery.trim().length < 2) {
      setRecipientOptions([]);
      return;
    }
    if (recipientDebounce.current) clearTimeout(recipientDebounce.current);
    recipientDebounce.current = setTimeout(async () => {
      setRecipientLoading(true);
      const res = await announcementApi.searchRecipients(recipientQuery.trim());
      setRecipientOptions(res.success && res.data ? res.data : []);
      setRecipientLoading(false);
    }, 350);
    return () => {
      if (recipientDebounce.current) clearTimeout(recipientDebounce.current);
    };
  }, [recipientQuery, audience.type]);

  // Recount whenever the audience changes, debounced — every keystroke in a
  // segment field would otherwise trigger an aggregate over the user base.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshPreview = useCallback(
    (next: Audience) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setPreviewLoading(true);
        const res = await announcementApi.preview(next);
        if (res.success && res.data) {
          setPreview({
            total: res.data.total,
            withPush: res.data.withPush,
            byPlatform: res.data.byPlatform || {},
          });
          setNotFound(res.data.notFound || []);
        }
        setPreviewLoading(false);
      }, 450);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    refreshPreview(audience);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, audience, refreshPreview]);

  const patchFilters = (patch: Partial<AudienceFilters>) =>
    setAudience((a) => ({ ...a, type: 'segment', filters: { ...(a.filters || {}), ...patch } }));

  const contentValid = title.trim().length >= 3 && message.trim().length >= 3;
  const channelsValid = inApp || push;
  const linkValid = !linkUrl || linkUrl.startsWith('/');

  const stepError = useMemo(() => {
    if (step === 0) {
      if (!contentValid) return 'عنوان و متن باید حداقل ۳ کاراکتر باشند';
      if (!channelsValid) return 'حداقل یکی از کانال‌ها باید فعال باشد';
      if (!linkValid) return 'مقصد باید یک مسیر داخلی باشد و با / شروع شود (مثل /user/orders)';
    }
    if (step === 1 && audience.type === 'specific' && !(audience.userIds || []).length) {
      return 'حداقل یک گیرنده انتخاب کنید';
    }
    return null;
  }, [step, contentValid, channelsValid, linkValid, audience]);

  const buildPayload = () => ({
    title: title.trim(),
    message: message.trim(),
    category: category || undefined,
    notificationType: notificationType.trim() || 'announcement',
    linkUrl: linkUrl.trim() || undefined,
    imageUrl: imageUrl.trim() || undefined,
    channels: { inApp, push },
    force,
    audience,
    scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
  });

  const persist = async (): Promise<string | null> => {
    setSaving(true);
    setError(null);
    const payload = buildPayload();
    const res = initial?._id
      ? await announcementApi.update(initial._id, payload as any)
      : await announcementApi.create(payload as any);
    setSaving(false);
    if (!res.success) {
      setError(res.message || 'خطا در ذخیرهٔ اطلاعیه');
      return null;
    }
    return res.data?._id ?? initial?._id ?? null;
  };

  const handleSaveOnly = async () => {
    const id = await persist();
    if (!id) return;
    enqueueSnackbar(scheduledAt ? 'اطلاعیه زمان‌بندی شد' : 'پیش‌نویس ذخیره شد', { variant: 'success' });
    onSaved();
  };

  const handleSaveAndSend = async () => {
    const id = await persist();
    if (!id) return;
    const res = await announcementApi.send(id);
    enqueueSnackbar(res.message || (res.success ? 'ارسال آغاز شد' : 'خطا در ارسال'), {
      variant: res.success ? 'success' : 'error',
    });
    onSaved();
  };

  const handleTest = async () => {
    if (!contentValid) {
      setError('برای تست، عنوان و متن را کامل کنید');
      return;
    }
    setSaving(true);
    const res = await announcementApi.sendTest({
      title: title.trim(),
      message: message.trim(),
      category: category || undefined,
      notificationType: notificationType.trim() || 'announcement',
      linkUrl: linkUrl.trim() || undefined,
      imageUrl: imageUrl.trim() || undefined,
    } as any);
    setSaving(false);
    enqueueSnackbar(res.message || (res.success ? 'ارسال شد' : 'خطا'), {
      variant: res.success ? 'success' : 'error',
    });
  };

  const audienceOptions = meta?.audiences ?? [
    { id: 'all' as AudienceType, label: 'همهٔ کاربران', description: '' },
  ];
  const filters = audience.filters || {};

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={initial ? 'ویرایش اطلاعیه' : 'اطلاعیهٔ جدید'}
      subtitle="ارسال همزمان اعلان درون‌برنامه و پوش‌نوتیفیکیشن"
      maxWidth="md"
      loading={saving}
      error={error}
      disableClose={saving}
      actions={
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <Button onClick={onClose} disabled={saving}>
            انصراف
          </Button>
          <Box sx={{ flex: 1 }} />
          {step > 0 && (
            <Button onClick={() => setStep((s) => s - 1)} disabled={saving}>
              قبلی
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setStep((s) => s + 1)}
              disabled={!!stepError || saving}
            >
              بعدی
            </Button>
          ) : (
            <>
              <Button
                startIcon={<SaveIcon />}
                onClick={() => void handleSaveOnly()}
                disabled={saving}
              >
                {scheduledAt ? 'زمان‌بندی' : 'ذخیرهٔ پیش‌نویس'}
              </Button>
              {!scheduledAt && (
                <Gate permission="admin_announcement_send">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={() => void handleSaveAndSend()}
                    disabled={saving || !!stepError}
                  >
                    ارسال به {fmt(preview?.total)} کاربر
                  </Button>
                </Gate>
              )}
            </>
          )}
        </Stack>
      }
    >
      <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {stepError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {stepError}
        </Alert>
      )}

      {/* ── 1) content ─────────────────────────────────────────────── */}
      {step === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              <TextField
                label="عنوان"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                fullWidth
                required
                helperText={`${fmt(title.length)} از ${fmt(TITLE_MAX)} کاراکتر`}
              />
              <TextField
                label="متن اطلاعیه"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                fullWidth
                required
                multiline
                minRows={4}
                helperText={`${fmt(message.length)} از ${fmt(MESSAGE_MAX)} کاراکتر — در پوش حدود دو خط اول دیده می‌شود`}
              />
              <Autocomplete
                freeSolo
                options={knownTypes}
                value={notificationType}
                onInputChange={(_e, v) => setNotificationType(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="نوع اعلان"
                    placeholder="announcement"
                    helperText="می‌توانید نوع دلخواه بنویسید (مثل black_friday) یا از فهرست انتخاب کنید. حروف کوچک انگلیسی، عدد و زیرخط."
                  />
                )}
              />
              <FormControl fullWidth>
                <InputLabel>دستهٔ اعلان</InputLabel>
                <Select
                  label="دستهٔ اعلان"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <MenuItem value="">
                    <Stack>
                      <Typography variant="body2">خودکار (پیشنهادی)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        از روی نوع اعلان تشخیص داده می‌شود
                      </Typography>
                    </Stack>
                  </MenuItem>
                  {(meta?.categories ?? []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Stack>
                        <Typography variant="body2">{c.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  دسته فقط کانال اعلان در اندروید و امکان بی‌صداکردن را تعیین می‌کند — لازم نیست دستی انتخابش کنید.
                </Typography>
              </FormControl>
              <TextField
                label="مقصد (اختیاری)"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                fullWidth
                placeholder="/user/orders"
                error={!linkValid}
                helperText="با لمس اعلان، کاربر به این مسیر می‌رود. باید مسیر داخلی باشد."
              />
              <TextField
                label="تصویر (اختیاری)"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                fullWidth
                placeholder="https://…"
                helperText="در اعلان‌های تصویری اندروید و iOS نمایش داده می‌شود."
              />
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <PushPreview title={title} message={message} imageUrl={imageUrl} />

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                  کانال‌های ارسال
                </Typography>
                <FormControlLabel
                  control={<Switch checked={inApp} onChange={(e) => setInApp(e.target.checked)} />}
                  label="اعلان درون‌برنامه (زنگوله)"
                />
                <FormControlLabel
                  control={<Switch checked={push} onChange={(e) => setPush(e.target.checked)} />}
                  label="پوش‌نوتیفیکیشن روی گوشی و مرورگر"
                />
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={<Switch checked={force} onChange={(e) => setForce(e.target.checked)} color="warning" />}
                  label="نادیده‌گرفتن تنظیمات کاربر"
                />
                <Typography variant="caption" color="text.secondary">
                  فقط برای اطلاعیه‌های واقعاً ضروری (قطعی سرویس، هشدار امنیتی).
                  در حالت عادی، کاربرانی که این دسته را بی‌صدا کرده‌اند یا در
                  «ساعات سکوت» هستند پوش دریافت نمی‌کنند.
                </Typography>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}

      {/* ── 2) audience ────────────────────────────────────────────── */}
      {step === 1 && (
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>مخاطبان</InputLabel>
            <Select
              label="مخاطبان"
              value={audience.type}
              onChange={(e) => setAudience({ type: e.target.value as AudienceType })}
            >
              {audienceOptions.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <Stack>
                    <Typography variant="body2">{a.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {audience.type === 'specific' && (
            <Stack spacing={1.5}>
              <Autocomplete
                multiple
                options={recipientOptions}
                value={recipients}
                loading={recipientLoading}
                filterOptions={(x) => x}
                noOptionsText={
                  recipientQuery.trim().length < 2
                    ? 'شمارهٔ موبایل یا نام را بنویسید…'
                    : 'کاربری پیدا نشد'
                }
                loadingText="در حال جستجو…"
                getOptionLabel={(o) =>
                  `${o.name || 'بدون نام'}${o.phoneNumber ? ` — ${o.phoneNumber}` : ''}`
                }
                isOptionEqualToValue={(a, b) => a._id === b._id}
                onInputChange={(_e, v) => setRecipientQuery(v)}
                onChange={(_e, value) => {
                  setRecipients(value);
                  setAudience({ type: 'specific', userIds: value.map((u) => u._id) });
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Stack>
                      <Typography variant="body2">{option.name || 'بدون نام'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[option.phoneNumber, option.email].filter(Boolean).join(' • ') || '—'}
                      </Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="گیرندگان"
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹ یا نام کاربر"
                    helperText="با شمارهٔ موبایل، نام یا ایمیل جستجو کنید."
                  />
                )}
              />

              <TextField
                label="یا فهرست شماره‌ها را بچسبانید"
                fullWidth
                multiline
                minRows={2}
                placeholder={'09121234567\n09351234567'}
                onBlur={(e) => {
                  const list = e.target.value
                    .split(/[\s,;]+/)
                    .map((v) => v.trim())
                    .filter(Boolean);
                  if (list.length === 0) return;
                  // شماره‌ها خام فرستاده می‌شوند؛ بک‌اند آن‌ها را به کاربر تبدیل
                  // می‌کند و می‌گوید کدام‌ها به کسی نخوردند.
                  setAudience({
                    type: 'specific',
                    // Array.from, not spread: the admin-panel tsconfig targets
                    // ES5, where spreading a Set needs --downlevelIteration.
                    userIds: Array.from(
                      new Set([...(audience.userIds || []), ...list]),
                    ),
                  });
                  e.target.value = '';
                }}
                helperText="هر شماره در یک خط یا جداشده با کاما — بعد از خروج از کادر افزوده می‌شوند."
              />

              {notFound.length > 0 && (
                <Alert severity="warning">
                  {notFound.length} مورد به هیچ کاربری نخورد و نادیده گرفته می‌شود:{' '}
                  {notFound.slice(0, 8).join('، ')}
                  {notFound.length > 8 ? ' …' : ''}
                </Alert>
              )}
            </Stack>
          )}

          {audience.type === 'segment' && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                فیلترها
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                فیلترهای پرشده با «و» ترکیب می‌شوند. خالی‌ها نادیده گرفته می‌شوند.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>پلتفرم</InputLabel>
                    <Select
                      multiple
                      label="پلتفرم"
                      value={filters.platforms || []}
                      onChange={(e) =>
                        patchFilters({ platforms: e.target.value as string[] })
                      }
                      renderValue={(v) => (v as string[]).join('، ')}
                    >
                      {(meta?.platforms ?? ['android', 'ios', 'web']).map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="عضویت در N روز اخیر"
                    value={filters.registeredWithinDays ?? ''}
                    onChange={(e) =>
                      patchFilters({
                        registeredWithinDays: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                    InputProps={{ endAdornment: <InputAdornment position="end">روز</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="فعال در N روز اخیر"
                    value={filters.activeWithinDays ?? ''}
                    onChange={(e) =>
                      patchFilters({
                        activeWithinDays: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                    InputProps={{ endAdornment: <InputAdornment position="end">روز</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="غیرفعال بیش از N روز"
                    value={filters.inactiveForDays ?? ''}
                    onChange={(e) =>
                      patchFilters({
                        inactiveForDays: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                    InputProps={{ endAdornment: <InputAdornment position="end">روز</InputAdornment> }}
                    helperText="برای بازگرداندن کاربران خاموش"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="حداقل سفارش"
                    value={filters.minOrders ?? ''}
                    onChange={(e) =>
                      patchFilters({ minOrders: e.target.value === '' ? undefined : Number(e.target.value) })
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="حداکثر سفارش"
                    value={filters.maxOrders ?? ''}
                    onChange={(e) =>
                      patchFilters({ maxOrders: e.target.value === '' ? undefined : Number(e.target.value) })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="حداقل گلاپوینت"
                    value={filters.minFigaPoints ?? ''}
                    onChange={(e) =>
                      patchFilters({
                        minFigaPoints: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.hasPushToken === true}
                        onChange={(e) => patchFilters({ hasPushToken: e.target.checked || undefined })}
                      />
                    }
                    label="فقط کاربرانی که اعلان را فعال کرده‌اند"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.kycVerified === true}
                        onChange={(e) => patchFilters({ kycVerified: e.target.checked || undefined })}
                      />
                    }
                    label="فقط احراز هویت‌شده‌ها"
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: theme.palette.primary.main,
              bgcolor: theme.palette.action.hover,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <PeopleIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                {previewLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2">در حال محاسبهٔ مخاطبان…</Typography>
                  </Stack>
                ) : (
                  <>
                    <Typography variant="body1" fontWeight={800}>
                      {fmt(preview?.total)} کاربر
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(preview?.withPush)} نفرشان پوش دریافت می‌کنند
                      {preview && Object.keys(preview.byPlatform).length > 0 && (
                        <>
                          {' — '}
                          {Object.entries(preview.byPlatform)
                            .map(([p, c]) => `${p}: ${fmt(c)}`)
                            .join('، ')}
                        </>
                      )}
                    </Typography>
                  </>
                )}
              </Box>
            </Stack>
            {push && preview && preview.withPush === 0 && preview.total > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                هیچ‌کدام از این کاربران دستگاهی با اعلان فعال ندارند — فقط اعلان
                درون‌برنامه دریافت می‌کنند.
              </Alert>
            )}
          </Paper>
        </Stack>
      )}

      {/* ── 3) schedule + review ───────────────────────────────────── */}
      {step === 2 && (
        <Stack spacing={2}>
          <TextField
            label="زمان ارسال"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ScheduleIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            helperText="خالی بگذارید تا بلافاصله ارسال شود."
          />

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
              مرور نهایی
            </Typography>
            <Stack spacing={1}>
              {[
                ['عنوان', title || '—'],
                ['مخاطب', audienceOptions.find((a) => a.id === audience.type)?.label ?? '—'],
                ['تعداد گیرنده', `${fmt(preview?.total)} کاربر (${fmt(preview?.withPush)} با پوش)`],
                ['نوع', notificationType || 'announcement'],
                [
                  'دسته',
                  category
                    ? meta?.categories.find((c) => c.id === category)?.label ?? category
                    : 'خودکار',
                ],
                [
                  'کانال‌ها',
                  [inApp && 'درون‌برنامه', push && 'پوش'].filter(Boolean).join(' + ') || '—',
                ],
                ['مقصد', linkUrl || '—'],
                ['زمان', scheduledAt ? new Date(scheduledAt).toLocaleString('fa-IR') : 'بلافاصله'],
              ].map(([label, value]) => (
                <Stack key={String(label)} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'left' }}>
                    {value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          {force && (
            <Alert severity="warning">
              «نادیده‌گرفتن تنظیمات کاربر» روشن است — این اطلاعیه حتی برای
              کاربرانی که اعلان این دسته را خاموش کرده‌اند ارسال می‌شود.
            </Alert>
          )}

          <Button
            variant="outlined"
            startIcon={<ScienceIcon />}
            onClick={() => void handleTest()}
            disabled={saving}
          >
            ارسال تستی به حساب خودم
          </Button>
          <Typography variant="caption" color="text.secondary">
            قبل از ارسال همگانی، یک نسخه برای حساب خودتان بفرستید تا متن و مقصد
            را روی گوشی ببینید.
          </Typography>
        </Stack>
      )}
    </ResponsiveModal>
  );
};

export default AnnouncementComposer;
